import { Link } from 'react-router-dom';
import { useAppointments, usePlanBalance } from '@/hooks/use-catalog';
import { AppointmentCard } from '@/components/appointment-card';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  SectionTitle,
  Skeleton,
  firstName,
  formatRelativeDay,
  formatTime,
  greeting,
  useAuthStore,
  useTenant,
} from '@barber/ui';
import { MessageCircle, Scissors, Star } from 'lucide-react';

/**
 * Início.
 *
 * ---
 *
 * A TELA RESPONDE TRÊS PERGUNTAS, NESTA ORDEM
 *
 *   1. quando é o meu próximo horário?
 *   2. o que meu plano ainda me dá este mês?
 *   3. como marco outro?
 *
 * É a ordem em que o cliente abre o app pensando. Um painel com estatísticas
 * bonitas e o próximo horário no rodapé responderia a pergunta certa no lugar
 * errado.
 */
export function HomePage() {
  const { tenant, locale } = useTenant();
  const user = useAuthStore((s) => s.user);

  const proximos = useAppointments('proximos');
  const plano = usePlanBalance();

  const proximo = proximos.data?.[0];
  const demais = proximos.data?.slice(1, 3) ?? [];

  return (
    <div className="flex flex-col gap-7">
      <section>
        <p className="text-sm text-content-muted">{greeting(tenant.timezone)},</p>
        <h1 className="text-2xl font-bold text-content">
          {user ? firstName(user.name) : 'tudo bem'}!
        </h1>
      </section>

      <section>
        <SectionTitle>Seu próximo horário</SectionTitle>

        {proximos.isPending && <Skeleton className="h-28 w-full" />}

        {proximos.isError && (
          <ErrorState
            description="Não conseguimos buscar seus horários agora."
            onRetry={() => void proximos.refetch()}
          />
        )}

        {proximos.isSuccess && !proximo && (
          <EmptyState
            icon={Scissors}
            title="Nenhum horário marcado"
            description="Escolha um serviço e garanta seu horário em menos de um minuto."
            action={
              <Link
                to="/agendar"
                className="tap-target inline-flex items-center rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-brand-fg"
              >
                Agendar agora
              </Link>
            }
          />
        )}

        {proximo && (
          <div className="flex flex-col gap-3">
            <Card className="border-brand/30 bg-brand/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-content-muted">
                    {formatRelativeDay(proximo.startsAt, locale)}
                  </p>
                  <p className="mt-1 text-3xl font-bold text-content">
                    {formatTime(proximo.startsAt, locale)}
                  </p>
                  <p className="mt-1 text-sm text-content">
                    {proximo.service.name} com {proximo.barber.name}
                  </p>
                </div>

                <Badge tone={proximo.status === 'CONFIRMED' ? 'success' : 'warning'}>
                  {proximo.status === 'CONFIRMED' ? 'Confirmado' : 'A confirmar'}
                </Badge>
              </div>

              <Link
                to={`/horarios/${proximo.id}`}
                className="mt-4 inline-block text-sm font-semibold text-brand underline"
              >
                Ver detalhes
              </Link>
            </Card>

            {demais.map((appointment) => (
              <AppointmentCard key={appointment.id} appointment={appointment} compact />
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle
          action={
            <Link to="/plano" className="text-xs font-semibold text-brand underline">
              ver plano
            </Link>
          }
        >
          Seu plano
        </SectionTitle>

        {plano.isPending && <Skeleton className="h-24 w-full" />}

        {plano.isSuccess && !plano.data.subscription && (
          <EmptyState
            icon={Star}
            title="Você ainda não tem plano"
            description="Planos dão cortes inclusos e desconto no que sobrar."
          />
        )}

        {plano.data?.subscription && (
          <Card className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-content">{plano.data.subscription.planName}</p>
              <Badge tone={plano.data.subscription.status === 'ACTIVE' ? 'success' : 'warning'}>
                {plano.data.subscription.status === 'ACTIVE' ? 'Ativo' : 'Pendente'}
              </Badge>
            </div>

            {plano.data.quotas.length === 0 && plano.data.discounts.length === 0 && (
              <p className="text-sm text-content-muted">Este plano não tem cotas por ciclo.</p>
            )}

            {plano.data.quotas.map((quota) => (
              <div key={quota.benefitId} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-content">{quota.label}</span>
                  <span className="font-semibold text-content">
                    {quota.remaining} de {quota.total}
                  </span>
                </div>

                {/*
                  A barra mostra o que RESTA, e não o que foi usado: a pergunta
                  do cliente é "ainda posso?", não "quanto gastei?".
                */}
                <div
                  className="h-2 overflow-hidden rounded-full bg-surface-muted"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={quota.total}
                  aria-valuenow={quota.remaining}
                  aria-label={`${quota.label}: ${quota.remaining} de ${quota.total} restantes`}
                >
                  <div
                    className="h-full rounded-full bg-brand transition-all"
                    style={{
                      width: `${quota.total > 0 ? (quota.remaining / quota.total) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}

            {plano.data.discounts.map((desconto) => (
              <p key={desconto.label} className="text-sm text-content-muted">
                {desconto.description}
              </p>
            ))}
          </Card>
        )}
      </section>

      {tenant.contact.whatsapp && (
        <section>
          <SectionTitle>Falar com a barbearia</SectionTitle>
          <Card>
            <a
              href={`https://wa.me/${tenant.contact.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noreferrer"
              className="tap-target flex items-center gap-3 text-sm font-medium text-content"
            >
              <MessageCircle className="h-5 w-5 text-sx-success" aria-hidden="true" />
              Chamar no WhatsApp
            </a>
          </Card>
        </section>
      )}

      {/*
        O botão flutuante fica SOBRE o conteúdo; sem este espaço, ele cobriria
        o último cartão da tela — que numa lista rolada até o fim é justamente
        o que a pessoa quer tocar.
      */}
      <div aria-hidden="true" className="h-14" />

      <Link
        to="/agendar"
        className="fixed inset-x-0 bottom-16 z-10 mx-auto w-[calc(100%-2rem)] max-w-[28rem] rounded-xl bg-brand py-4 text-center text-sm font-semibold text-brand-fg shadow-lg"
      >
        Agendar horário
      </Link>
    </div>
  );
}
